/**
 * Ждать ответа сервера по генерации, сколько бы она ни шла (план «Ждём ответа нейросети», Ф2–Ф3, 26.09).
 *
 * Исход генерации решает ответ нейросети: сервер закрывает генерацию им сам (сверка без потолка), а фронт просто
 * спрашивает, чем дело кончилось, — всё реже, чем дольше идёт генерация (pollDelayMs). Прежние потолки опроса
 * (24 минуты в студии, 5–10 минут у музыки) объявляли «Таймаут», пока нейросеть ещё рисовала.
 *
 * Останавливает ожидание только ответ сервера, «генерации нет» (404/403/422) или сам вызывающий (shouldStop:
 * размонтировали, отменили — вернётся null). Без входа сервер не спрашиваем, а ждём: ответ 401 уводит на /login
 * (api/client), и запись с номером, которая теперь живёт до ответа сервера, перезагружала бы страницу входа по кругу.
 * Вошёл другой человек — ожидание кончается (null): его сессия чужие генерации не доводит (utils/sessionUser).
 */
import { getTaskStatus, isTaskGone, TASK_GONE_MESSAGE, type TaskStatusResponse } from '../../../api/images'
import { pendingOwner } from '../../../utils/forgetPendingGenerations'
import { isOtherUser, sessionUserId } from '../../../utils/sessionUser'
import { pollDelayMs } from './constants'

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export interface PollOptions {
	/** Минимальная пауза между вопросами (у музыки — 10 с, как было). */
	minDelayMs?: number
	/** Когда генерация началась — от этого считается затухание частоты (по умолчанию — старт ожидания). */
	startedAt?: number
	/** Прекратить ожидание (размонтировали, отменили) — вернётся null. */
	shouldStop?: () => boolean
}

/**
 * Спрашивать check, пока он не вернёт ответ (не undefined). Сбой связи — спросим снова; «генерации нет»
 * (404/422) отдаётся в onGone, и ожидание кончается его ответом.
 */
export async function pollUntil<T>(
	check: () => Promise<T | undefined>,
	opts: PollOptions & { onGone?: () => T } = {},
): Promise<T | null> {
	const started = opts.startedAt ?? Date.now()
	// Хозяин ожидания — чьи записи о генерациях в браузере (ожидание могли поднять из них после перезагрузки, ещё до
	// входа), иначе — кто вошёл сейчас.
	let owner = pendingOwner() || sessionUserId()
	for (;;) {
		await sleep(Math.max(opts.minDelayMs ?? 0, pollDelayMs(Date.now() - started)))
		if (opts.shouldStop?.()) return null
		const current = sessionUserId()
		if (!current && current !== '') continue // вышли из аккаунта — ждём входа, сервер не спрашиваем
		if (isOtherUser(owner, current)) return null // вошёл другой человек — генерация не его
		owner = owner || current
		try {
			const answer = await check()
			if (opts.shouldStop?.()) return null
			if (answer !== undefined) return answer
		} catch (e) {
			if (isTaskGone(e) && opts.onGone) return opts.onGone()
			// сбой связи — спросим на следующем круге
		}
	}
}

export interface WaitForTaskOptions extends PollOptions {
	/** Готово ли. По умолчанию — completed; аудио ждёт ещё и ссылки. Ответ failed завершает всегда. */
	isDone?: (s: TaskStatusResponse) => boolean
}

/** Ждать ответа сервера по генерации requestId: вернёт её статус (completed / failed) или null (shouldStop). */
export function waitForTask(requestId: string, opts: WaitForTaskOptions = {}): Promise<TaskStatusResponse | null> {
	const isDone = opts.isDone ?? ((s: TaskStatusResponse) => s.status === 'completed')
	return pollUntil<TaskStatusResponse>(
		async () => {
			const s = await getTaskStatus(requestId)
			return s.status === 'failed' || isDone(s) ? s : undefined
		},
		{
			...opts,
			onGone: () => ({ request_id: requestId, status: 'failed', type: '', model: '', error_message_ru: TASK_GONE_MESSAGE }),
		},
	)
}
